/**
 * Schema Service for dev-tools.
 *
 * Discovers method definitions for a configured service via protobuf
 * reflection and caches the per-service result.
 */
import { AVAILABLE_SERVICES } from "../constants";
import { MethodDefinition, ServiceInfo } from "../types";
import { logger } from "../utils/logger";
import { ProtobufSchemaExtractor } from "../utils/schema-extractor";

export class SchemaService {
  private static methodCache: Map<string, MethodDefinition[]> = new Map();

  static getAvailableServices(): ServiceInfo[] {
    return AVAILABLE_SERVICES.map((service) => ({
      id: service.id,
      name: service.name,
      description: service.description,
      methodCount: SchemaService.getMethodCountForService(service.id),
    }));
  }

  private static getMethodCountForService(serviceId: string): number {
    try {
      const serviceDefinition = AVAILABLE_SERVICES.find(
        (s) => s.id === serviceId
      );
      if (!serviceDefinition?.service?.methods) {
        return 0;
      }
      return Object.keys(serviceDefinition.service.methods).length;
    } catch (error) {
      logger.warn(`Failed to get method count for service ${serviceId}`, {
        error,
      });
      return 0;
    }
  }

  static async getMethodDefinitionsForService(
    serviceId: string
  ): Promise<MethodDefinition[]> {
    try {
      const cached = this.methodCache.get(serviceId);
      if (cached) {
        return cached;
      }

      const serviceDefinition = AVAILABLE_SERVICES.find(
        (s) => s.id === serviceId
      );
      if (!serviceDefinition) {
        throw new Error(`Service ${serviceId} not found`);
      }

      const methodSchemas =
        ProtobufSchemaExtractor.extractMethodSchemasForService(
          serviceDefinition.service,
          serviceId
        );

      const methodDefinitions: MethodDefinition[] = methodSchemas.map(
        (schema) => ({
          name: schema.name,
          description: schema.description,
          inputSchema: schema.fields,
          serviceId,
        })
      );

      this.methodCache.set(serviceId, methodDefinitions);

      return methodDefinitions;
    } catch (error) {
      logger.error(
        `Failed to extract method schemas for service ${serviceId}`,
        { error }
      );
      throw new Error(`Failed to load method definitions for ${serviceId}`);
    }
  }

  static clearCache(): void {
    this.methodCache.clear();
  }
}
